import { type MouseEvent, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Typography, Box } from '@mui/material';
import { Form, Formik } from 'formik';

import { useSocialAuth } from '@/contexts/socialAuth';
import { useCaptcha } from '@/hooks/useCaptcha';

import { OAuthProvider } from '@/enums/socialAuth';
import { isResponseError } from '@/utils/errorUtils';
import { hasFeature } from '@/utils/featureFlag';
import { setRecord } from '@/utils/storageUtils';

import { FormikField } from '@/components/forms/formik/FormikField';
import { FormikSubmit } from '@/components/forms/formik/FormikSubmit';
import { FormikNewPassword } from '@/components/forms/formik/newPassword/FormikNewPassword';
import { FormikPasswordRules } from '@/components/forms/formik/newPassword/FormikPasswordRules';
import { SocialButton } from '@/components/socialButton';
import { TextField } from '@/shared/textField/TextField';

import type { TOnSubmit } from '@/types/formTypes';

import { finishReferralSignUp, signup } from '@/store/profile/profile.actions';
import { selectSignUpError, selectSignUpLoading } from '@/store/profile/profile.selectors';
import { ActionEvents, trackEvent } from '@/tracking';
import { signupSchema } from '@/validation/signupSchema';

import {
  useStyles as useFormStyles,
  AuthFormContent,
  AuthFormTitle,
  AuthFormDivider,
} from '../AuthForms.styles';
import type { ISignupForm } from '../types';

import { AcceptanceDialog } from './AcceptanceDialog/AcceptanceDialog';
import { DuplicateEmailBox } from './DuplicateEmailBox';
import { useStyles } from './SignupForm.styles';
import type { TConsentPopup, TSignupFormProps } from './types';

export const SignupForm = ({ invitationEmail, encryptedPayload }: TSignupFormProps) => {
  const formClasses = useFormStyles();
  const signupClasses = useStyles();
  const dispatch = useDispatch();
  const titleRef = useRef<HTMLElement>(null);

  const { t } = useTranslation();
  const { requestAuth } = useSocialAuth();

  const loading = useSelector(selectSignUpLoading);
  const serverError = useSelector(selectSignUpError);
  const captchaHelper = useCaptcha('signup', serverError);
  const submitButton = useRef<HTMLButtonElement>(null);

  const [consentPopup, setConsentPopup] = useState<TConsentPopup>(null);

  const submitViaEmail: TOnSubmit<ISignupForm> = (
    { fullName, email, password },
    { setSubmitting },
  ) => {
    if (!submitButton.current) {
      return;
    }

    setSubmitting(false);
    setConsentPopup({
      anchor: submitButton.current,
      onContinue: (consentInfo, captcha) => {
        const body = {
          ...consentInfo,
          fullName: fullName.trim(),
          email: email.trim(),
          password,
          captcha,
        };

        trackEvent(ActionEvents.signupStarted);

        if (encryptedPayload) {
          // signup from referral link
          dispatch(finishReferralSignUp.request({ encryptedPayload, body }));
        } else {
          dispatch(signup.request(body));
        }
      },
    });
  };

  const submitViaSocial = (
    { currentTarget }: MouseEvent<HTMLButtonElement>,
    provider: OAuthProvider,
  ) => {
    if (provider === OAuthProvider.soundCloud) {
      requestAuth(provider);

      return;
    }

    setConsentPopup({
      anchor: currentTarget,
      onContinue: (consentInfo) => {
        setRecord('profile:consentInfo', consentInfo, { useOnce: true });
        requestAuth(provider);
      },
    });
  };

  const tryCloseConsentPopup = () => {
    if (!loading) {
      setConsentPopup(null);
    }
  };

  const hasResponseError = isResponseError(serverError, 409, 'error.email.duplicate');

  useEffect(() => {
    if (hasResponseError && !loading) {
      setConsentPopup(null);
    }
  }, [hasResponseError, loading]);

  return (
    <AuthFormContent>
      <AuthFormTitle ref={titleRef}>{t('landing.signup.title')}</AuthFormTitle>

      <div className={signupClasses.body}>
        <Formik
          initialValues={signupSchema.cast({ email: invitationEmail })}
          validationSchema={signupSchema}
          onSubmit={submitViaEmail}
          validateOnChange={false}
        >
          <Form className={formClasses.form}>
            {hasResponseError && <DuplicateEmailBox />}

            <FormikField
              name="fullName"
              label="landing.signup.fullNameLabel" // displayed as First Name on UI
              placeholder="landing.signup.fullNamePlaceholder"
              Component={TextField}
              errorAllowance={1.5}
            />

            <FormikField
              name="email"
              type="email"
              label="landing.welcome.emailLabel"
              placeholder="landing.welcome.emailPlaceholder"
              Component={TextField}
              disabled={Boolean(invitationEmail)}
              errorAllowance={1.5}
            />

            <FormikNewPassword />
            <FormikPasswordRules />

            <FormikSubmit ref={submitButton} className={formClasses.continueButton}>
              {t('landing.signup.signUpForFree')}
            </FormikSubmit>
          </Form>
        </Formik>

        <Typography variant="body2" align="center" className={signupClasses.noCardRequired}>
          {t('landing.signup.noCreditCardRequired')}
        </Typography>

        <AuthFormDivider>
          <Typography variant="body2">{t('landing.welcome.divider')}</Typography>
        </AuthFormDivider>

        <Box display="flex" flexDirection="column" gap={2}>
          <SocialButton
            provider={OAuthProvider.google}
            onClick={(e) => submitViaSocial(e, OAuthProvider.google)}
          >
            {t('landing.signup.google')}
          </SocialButton>

          {hasFeature('soundcloudTrackUpload') && (
            <SocialButton
              provider={OAuthProvider.soundCloud}
              onClick={(e) => {
                trackEvent(ActionEvents.signUpWithSoundCloudClick);
                submitViaSocial(e, OAuthProvider.soundCloud);
              }}
            >
              {t('landing.signup.soundCloud')}
            </SocialButton>
          )}
        </Box>

        <AcceptanceDialog
          popup={consentPopup}
          captcha={captchaHelper}
          onClose={tryCloseConsentPopup}
          loading={loading}
          titleRef={titleRef}
        />
      </div>
    </AuthFormContent>
  );
};
